import { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import axios from "axios";
import AdminLayout from "./AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Package, BookOpen, UserCheck, UserX } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const { getAuthHeader } = useAuth();
  const [stats, setStats] = useState({
    packages: 0,
    lessons: 0,
    activeUsers: 0,
    passiveUsers: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [packagesRes, lessonsRes, usersRes] = await Promise.all([
        axios.get(`${API}/admin/packages`, getAuthHeader()),
        axios.get(`${API}/admin/lessons`, getAuthHeader()),
        axios.get(`${API}/admin/users`, getAuthHeader())
      ]);

      setStats({
        packages: packagesRes.data.length,
        lessons: lessonsRes.data.length,
        activeUsers: usersRes.data.active_count,
        passiveUsers: usersRes.data.passive_count
      });
    } catch (error) {
      console.error("Error fetching stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { label: "Toplam Paket", value: stats.packages, icon: Package, color: "bg-red-600/20 text-red-500" },
    { label: "Toplam Ders", value: stats.lessons, icon: BookOpen, color: "bg-blue-600/20 text-blue-500" },
    { label: "Aktif Kullanıcı", value: stats.activeUsers, icon: UserCheck, color: "bg-emerald-600/20 text-emerald-500" },
    { label: "Pasif Kullanıcı", value: stats.passiveUsers, icon: UserX, color: "bg-amber-600/20 text-amber-500" },
  ];

  return (
    <AdminLayout title="Dashboard">
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
        </div>
      ) : (
        <div className="space-y-8">
          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {statCards.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <Card key={index} className="border-neutral-800 bg-neutral-900" data-testid={`stat-card-${index}`}>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-neutral-400">{stat.label}</p>
                        <p className="text-3xl font-bold text-white mt-1">{stat.value}</p>
                      </div>
                      <div className={`w-12 h-12 rounded-xl ${stat.color} flex items-center justify-center`}>
                        <Icon className="w-6 h-6" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Quick Actions */}
          <Card className="border-neutral-800 bg-neutral-900">
            <CardHeader>
              <CardTitle className="text-lg font-bold text-white font-['Nunito']">Hızlı Erişim</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <a href="/admin/packages" className="p-4 rounded-xl bg-neutral-800 hover:bg-neutral-700 transition-colors text-center group">
                  <Package className="w-8 h-8 text-red-500 mx-auto mb-2 group-hover:scale-110 transition-transform" />
                  <p className="font-medium text-white">Paket Ekle</p>
                </a>
                <a href="/admin/lessons" className="p-4 rounded-xl bg-neutral-800 hover:bg-neutral-700 transition-colors text-center group">
                  <BookOpen className="w-8 h-8 text-blue-500 mx-auto mb-2 group-hover:scale-110 transition-transform" />
                  <p className="font-medium text-white">Ders Ekle</p>
                </a>
                <a href="/admin/users" className="p-4 rounded-xl bg-neutral-800 hover:bg-neutral-700 transition-colors text-center group">
                  <UserCheck className="w-8 h-8 text-emerald-500 mx-auto mb-2 group-hover:scale-110 transition-transform" />
                  <p className="font-medium text-white">Kullanıcı Ekle</p>
                </a>
                <a href="/admin/pricing" className="p-4 rounded-xl bg-neutral-800 hover:bg-neutral-700 transition-colors text-center group">
                  <span className="w-8 h-8 text-amber-500 mx-auto mb-2 block text-2xl group-hover:scale-110 transition-transform">€</span>
                  <p className="font-medium text-white">Fiyat Güncelle</p>
                </a>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </AdminLayout>
  );
}
